import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, Route, Routes, useParams} from 'react-router-dom';

import {ACCOUNT_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {BasicLicenseTabStructure} from 'src/modules/profile/account/tabs/license-and-features/basic-tab-scructure';

import {AccountConnectorLimitsTab} from './connector-limits-tab';
import {LICENSE_AND_FEATURES_SUB_TABS} from './constants';
import {CrmTab} from './crm-tab';
import {FeaturesAndLimitsTab} from './features-and-limits-tab';
import {NewSsoTab} from './new-sso-tab';
import {ReportTab} from './report-tab';
import {clearAccountLicense} from './services/account-license-features-slice';

export const LicenseFeaturesTabView = () => {
    const queryParams = useParams<{accountId: string, licenseId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const licenseId = queryParams.licenseId ? +queryParams.licenseId : -1;

    const dispatch = useDispatch();
    const {hasAccess, canViewAccountConnectorLimits} = useAdminAccessRight();
    const {hierarchy} = useSelector(selectAccountDetails);

    const isReportsAllowed = hasAccess(ACCESS_RIGHTS.ACCOUNT_REPORTS_SCREEN_VIEW);
    const isSsoAllowed = useMemo(
        () => hasAccess(ACCESS_RIGHTS.SSO_SCREEN_VIEW) && hierarchy?.current?.entityTypeCode !== 'Event',
        [hierarchy, hasAccess],
    );
    const isConnectorLimitsAllowed = canViewAccountConnectorLimits(accountId);

    useEffect(() => {
        dispatch(clearAccountLicense());
    }, [licenseId, dispatch]);

    return (
        <Routes>
            <Route
                path={`/${LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.name}`}
                element={(
                    <BasicLicenseTabStructure
                        main={<FeaturesAndLimitsTab/>}
                        accountId={accountId}
                        licenseId={licenseId}
                        activeSubTab={LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.name}
                    />
                )}
            />

            <Route
                path={`/${LICENSE_AND_FEATURES_SUB_TABS.CRM.name}`}
                element={(
                    <BasicLicenseTabStructure
                        main={<CrmTab/>}
                        accountId={accountId}
                        licenseId={licenseId}
                        activeSubTab={LICENSE_AND_FEATURES_SUB_TABS.CRM.name}
                    />
                )}
            />

            {isSsoAllowed ? (
                <Route
                    path={`/${LICENSE_AND_FEATURES_SUB_TABS.SSO.name}`}
                    element={(
                        <BasicLicenseTabStructure
                            main={<NewSsoTab/>}
                            accountId={accountId}
                            licenseId={licenseId}
                            activeSubTab={LICENSE_AND_FEATURES_SUB_TABS.SSO.name}
                        />
                    )}
                />
            ) : null}

            {isReportsAllowed ? (
                <Route
                    path={`/${LICENSE_AND_FEATURES_SUB_TABS.REPORTS.name}/*`}
                    element={(
                        <BasicLicenseTabStructure
                            main={<ReportTab accountId={accountId} licenseId={licenseId}/>}
                            accountId={accountId}
                            licenseId={licenseId}
                            activeSubTab={LICENSE_AND_FEATURES_SUB_TABS.REPORTS.name}
                        />
                    )}
                />
            ) : null}

            {isConnectorLimitsAllowed ? (
                <Route
                    path={`/${LICENSE_AND_FEATURES_SUB_TABS.CONNECTOR_LIMITS.name}`}
                    element={(
                        <BasicLicenseTabStructure
                            main={<AccountConnectorLimitsTab accountId={accountId}/>}
                            accountId={accountId}
                            licenseId={licenseId}
                            activeSubTab={LICENSE_AND_FEATURES_SUB_TABS.CONNECTOR_LIMITS.name}
                        />
                    )}
                />
            ) : null}

            <Route
                path="*"
                element={(
                    <Navigate

                        to={`/profile/account/${accountId}/${ACCOUNT_TABS.LICENSE.name}/${licenseId}/${LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.name}`}
                    />
                )}
            />
        </Routes>
    );
};
